/**
 * WordsReveal — split a string into words and stagger-fade them up.
 *
 * Use for hero headlines: each word fades in from 24px below with a
 * cubic ease and an ~80ms stagger. Reduced-motion → instant opacity.
 *
 *   <WordsReveal as="h1" text={t('home.heroHeadline')} />
 */
import { motion } from "framer-motion";
import usePreferStaticUI from "@/lib/usePreferStaticUI";

const EASE = [0.22, 0.61, 0.36, 1];

export const WordsReveal = ({
  text,
  as = "h1",
  delay = 0,
  stagger = 0.08,
  duration = 0.7,
  y = 24,
  className,
  testId,
}) => {
  const reduced = usePreferStaticUI();
  const Tag = as;

  if (reduced) {
    return (
      <Tag className={className} data-testid={testId}>
        {text}
      </Tag>
    );
  }

  const MotionTag = motion[as] || motion.h1;
  const words = (text || "").split(/\s+/).filter(Boolean);

  return (
    <MotionTag
      className={className}
      data-testid={testId}
      initial="hidden"
      animate="show"
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: stagger, delayChildren: delay } },
      }}
      aria-label={text}
    >
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-baseline">
          <motion.span
            aria-hidden="true"
            className="inline-block will-change-transform"
            variants={{
              hidden: { y, opacity: 0 },
              show: { y: 0, opacity: 1, transition: { duration, ease: EASE } },
            }}
          >
            {w}
            {i < words.length - 1 && "\u00A0"}
          </motion.span>
        </span>
      ))}
    </MotionTag>
  );
};

export default WordsReveal;
